import React, { createElement, useState, useEffect } from 'react';
import { Chart, Tooltip, Interval, Coordinate, Axis, Legend, Slider, registerShape, } from 'bizcharts';
import { Message } from '@alifd/next';
import './index.scss';
import { previewDataByField } from '../../../api/common/index';
import { dealArray, dealOrderByBoList, dealFieldByList } from '../../../utils';
import ErrorBoundary from '../../common/errorBoundary';
import ErrorComponent from '../../common/errorComponent';
registerShape('interval', 'border-radius', {
    draw(cfg, container) {
        const { points, color, defaultStyle } = cfg;
        let path = [];
        path.push(['M', points[0].x, points[0].y]);
        path.push(['L', points[1].x, points[1].y]);
        path.push(['L', points[2].x, points[2].y]);
        path.push(['L', points[3].x, points[3].y]);
        path.push('Z');
        path = this.parsePath(path);
        const style = { ...defaultStyle };
        if (color) {
            style.fill = color;
        }
        const group = container.addGroup();
        const width = path[2][1] - path[1][1];
        const height = width / 2;
        const radius = width / 2;
        const disY = path[0][2] - path[1][2];
        const startY = path[0][2] - height / 2;
        const endY = disY > 0 ? path[1][2] + height / 2 : path[1][2] - height / 2;
        const num = width / 4;
        const h = startY - endY;
        const flag = h > radius;
        group.addShape('rect', {
            attrs: {
                x: path[1][1],
                y: endY + (flag ? num : -num),
                width,
                height: disY + (flag ? -2 * num : 0),
                fill: style.fill,
            },
            zIndex: 0,
        });
        if (flag) {
            group.addShape('rect', {
                attrs: {
                    x: path[1][1],
                    y: endY - num,
                    width,
                    height: width,
                    fill: style.fill,
                    radius,
                },
                zIndex: 2,
            });
        }
        return group;
    },
});
const ABarChart = (props, ref) => {
    const { dataSource, data, adjust, isBar, styleType, theme, color, xAxis, yAxis, legend, label, slider, tooltip, barCircle, hasTitle, titleText, } = props;
    const [error, setError] = useState(false);
    const [errmsg, setErrmsg] = useState(null);
    const [idata, setIdata] = useState([]);
    const [xProps, setXprops] = useState('item');
    const [yProps, setYprops] = useState('value');
    const [series, setSeries] = useState('name');
    useEffect(() => {
        setError(false);
        try {
            if (dataSource?.xaxis?.length && dataSource?.yaxis?.length) {
                const limit = dataSource?.limit || [100];
                if (limit[0] > 1000) {
                    Message.error('最多显示前1000条!');
                    return;
                }
                const fl = dataSource?.filter || [];
                const params = {
                    groupId: dataSource.dataset.id,
                    type: 'BarChart',
                    fieldList: dealFieldByList([
                        ...dataSource?.xaxis,
                        ...dataSource?.yaxis,
                        ...(dataSource?.group || []),
                    ]),
                    horizontalAxis: dealArray(dataSource.xaxis),
                    longitudinalAxis: dealArray(dataSource.yaxis)?.map((e) => ({
                        ...e,
                        aggregateType: e?.aggregateType || 'COUNT',
                    })),
                    groupAxis: dealArray(dataSource?.group || []),
                    orderByBoList: dealOrderByBoList([...dataSource?.xaxis, ...(dataSource?.group || [])]),
                    resultCount: limit[0] === undefined ? 100 : limit[0],
                    customFilterVo: fl[0] === undefined
                        ? null
                        : {
                            logic: 'and',
                            filterVos: [dataSource?.filter[0] || null].filter((e) => e),
                        },
                };
                previewDataByField(params).then((res) => {
                    const fields = res?.data?.fields;
                    if (!fields) {
                        Message.error(res?.msg);
                        return;
                    }
                    const xp = fields.find((e) => e.id === dataSource.xaxis[0].id)?.fieldShortName || 'defalut';
                    const yp = fields.find((e) => e.id === dataSource.yaxis[0].id)?.fieldShortName || 'defalut';
                    const se = fields.find((e) => e.id === dataSource.group[0]?.id)?.fieldShortName || 'defalut';
                    setXprops(xp);
                    setYprops(yp);
                    setSeries(se);
                    let tempData = res.data.data.map((e) => {
                        for (let item of dataSource.yaxis) {
                            e[item.fieldShortName] = Number(e[item.fieldShortName]);
                        }
                        if (!dataSource.group.length) {
                            e.defalut = '默认';
                        }
                        return e;
                    });
                    if (dataSource?.xaxis[0]?.additional?.sort) {
                        const sortArr = dataSource?.xaxis[0]?.additional?.sort.filter(e => e.checked);
                        const sortList = [];
                        sortArr.forEach(item => {
                            tempData.forEach(itm => {
                                if (itm[dataSource?.xaxis[0]?.fieldShortName] === item.name) {
                                    sortList.push(itm);
                                }
                            });
                        });
                        tempData = sortList;
                    }
                    setIdata(tempData);
                });
            }
            else {
                setXprops('item');
                setYprops('value');
                setSeries('name');
                setIdata(data);
            }
        }
        catch (error) {
            console.log(error);
            setError(true);
            setErrmsg('初始化数据出错,请在数据源面板重新配置字段或更新组件');
        }
    }, [dataSource]);
    const [myTheme, setMyTheme] = useState({});
    const [mySize, setMySize] = useState(15);
    useEffect(() => {
        try {
            if (theme) {
                const { maxColumnWidth, minColumnWidth, size } = theme;
                setMyTheme({ maxColumnWidth, minColumnWidth });
                setMySize(Math.min(Math.max(size, minColumnWidth), maxColumnWidth));
            }
        }
        catch (error) {
            console.log(error);
            setError(true);
            setErrmsg('柱体宽度配置出错,请在属性面板检查宽度字段');
        }
    }, [props.theme]);
    const colorArr = color ? color?.split(',') : [];
    return (createElement(ErrorBoundary, null, error ? (createElement(ErrorComponent, { ref: ref, errmsg: errmsg })) : (createElement("div", { ref: ref, className: "base_bg" },
        hasTitle ? (createElement("div", { className: "datasettitle" }, titleText || dataSource?.dataset?.name)) : null,
        createElement(Chart, { height: 400, padding: "auto", theme: styleType === 'custom' ? myTheme : {}, data: idata, autoFit: true, scale: {
                [yProps]: {
                    type: 'linear',
                    min: yAxis?.min || 0,
                    max: yAxis?.max || null,
                    tickCount: yAxis?.tickCount,
                },
            }, onGetG2Instance: (e) => e.render(), forceUpdate: true },
            createElement(Coordinate, { transpose: isBar }),
            createElement(Axis, Object.assign({ name: xProps }, {
                visible: xAxis?.visible,
                line: xAxis?.line,
                tickLine: xAxis?.tickLine,
                title: xAxis?.showTitle && {
                    text: xAxis?.title,
                },
                label: xAxis?.label &&
                    (xAxis?.labelType === 'defalut'
                        ? {}
                        : {
                            style: {
                                fill: xAxis?.labelColor,
                                fontSize: xAxis?.labelSize,
                                width: xAxis?.labelLength === 'fixed'
                                    ? xAxis?.labelFixedWidth
                                    : xAxis?.labelPercentWidth,
                            },
                            autoHide: xAxis?.autoHide,
                            autoRotate: xAxis?.autoRotate,
                            ...{
                                ...(xAxis?.autoRotate
                                    ? {}
                                    : { rotate: (xAxis?.labelRotateAngle * 2 * Math.PI) / 180 }),
                            },
                        }),
                grid: xAxis?.grid,
            })),
            createElement(Axis, Object.assign({ name: yProps }, {
                visible: yAxis?.visible,
                line: yAxis?.line,
                tickLine: yAxis?.tickLine,
                title: yAxis?.showTitle && {
                    text: yAxis?.title,
                },
                label: yAxis?.label &&
                    (yAxis?.labelType === 'defalut'
                        ? {}
                        : {
                            style: {
                                fill: yAxis?.labelColor,
                                fontSize: yAxis?.labelSize,
                                width: yAxis?.labelLength === 'fixed'
                                    ? yAxis?.labelFixedWidth
                                    : yAxis?.labelPercentWidth,
                            },
                            autoHide: yAxis?.autoHide,
                            autoRotate: yAxis?.autoRotate,
                            ...(yAxis?.autoRotate
                                ? {}
                                : { rotate: (yAxis?.labelRotateAngle * 2 * Math.PI) / 180 }),
                        }),
                grid: yAxis?.grid,
            })),
            createElement(Legend, Object.assign({}, legend)),
            createElement(Interval, Object.assign({ adjust: adjust?.type, shape: [
                    `1*2`,
                    (date, val) => {
                        if (val === 0) {
                            return;
                        }
                        if (isBar || !barCircle) {
                            return 'rect';
                        }
                        return 'border-radius';
                    },
                ], label: {
                    fields: label?.visible ? [yProps] : [],
                    cfg: label?.displayType === 'custom'
                        ? {
                            style: {
                                fontSize: label?.style?.fontSize,
                                fill: label?.style?.autoFill ? '#000000' : label.style?.fill,
                            },
                            ...(label?.style?.autoPosition ? {} : { position: label?.style?.position }),
                        }
                        : {},
                } }, (styleType === 'custom' ? { size: mySize } : {}), { color: [series, colorArr], position: `${xProps}*${yProps}` })),
            createElement(Tooltip, Object.assign({ shared: true }, tooltip)),
            slider?.visible ? createElement(Slider, Object.assign({}, slider)) : '')))));
};
export default React.forwardRef(ABarChart);
